import {
  Avatar,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Link as ChakraLink,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { Flame, Play, Video } from "lucide-react";
import { MdChevronRight } from "react-icons/md";
import QRCode from "qrcode.react";
import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AnnouncementsCarousel from "../../components/student/AnnouncementsCarousel";
import DailyChallengeMenu from "../../components/student/DailyChallengeMenu";
import HomeAnalyticsCharts from "../../components/student/HomeAnalyticsCharts";
import MissedQuestions from "../../components/student/Cards/MissedQuestions";
import Layout from "../../components/student/Layout/layout";
import { getActiveAnnouncementsRequest } from "../../modules/students-modules/announcements/Actions";
import { getAllStudentChaptersRequest } from "../../modules/students-modules/chapters/Actions";
import { getPerformanceAnalyticsRequest } from "../../modules/students-modules/performance/Actions";
import { getAllSectionsRequest } from "../../modules/students-modules/sections/Actions";
import {
  getLiveVideoRequest,
  setLiveVideoSeenRequest,
} from "../../modules/students-modules/videos/Actions";

const C = {
  bg: "#FAF7F2",
  primary: "#E85D04",
  secondary: "#9A3412",
  border: "#E8E0D5",
  cream: "#FFF7ED",
  paper: "#FFFFFF",
  mutedBg: "#F5F0E8",
  muted: "#78716C",
};

function scanAttendanceUrl(userId) {
  return `https://asquera.com/scan-code/${userId}/attendence`;
}

const StudentHome = ({ t }) => {
  const baseUrl = "https://asquera.com/asquera";
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user.userData);
  const liveVideo = useSelector((state) => state.chapterVideos.liveVideo);
  const announcements = useSelector((state) => state.studentAnnouncements.announcements);
  const sections = useSelector((state) => state.sections.sections);
  const performanceData = useSelector((state) => state.performance.analytics);
  const perfLoading = useSelector((state) => state.performance.isLoading);
  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const { isOpen: isChallengeOpen, onOpen: onChallengeOpen, onClose: onChallengeClose } =
    useDisclosure();

  useEffect(() => {
    dispatch(getAllStudentChaptersRequest());
    dispatch(getAllSectionsRequest());
    dispatch(getLiveVideoRequest());
    dispatch(getActiveAnnouncementsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (userData?.id) {
      dispatch(getPerformanceAnalyticsRequest(userData.id));
    }
  }, [dispatch, userData?.id]);

  useEffect(() => {
    const hasSeenAnnouncements = localStorage.getItem("hasSeenAnnouncements");
    if (!hasSeenAnnouncements && announcements && announcements.length > 0) {
      setShowAnnouncements(true);
    }
  }, [announcements]);

  const handleCloseAnnouncements = () => {
    setShowAnnouncements(false);
    localStorage.setItem("hasSeenAnnouncements", "true");
  };

  const courseSections = useMemo(() => {
    const raw = sections;
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : [];
    const subjectId = userData?.subject?.id;
    return list.filter((s) => {
      if (subjectId == null || subjectId === undefined) return true;
      if (s.subject_id == null || s.subject_id === undefined) return true;
      return Number(s.subject_id) === Number(subjectId);
    });
  }, [sections, userData?.subject?.id]);

  const hasLiveVideo = Array.isArray(liveVideo) && liveVideo.length > 0;

  return (
    <Layout color="main_1" bg="white">
      <Box
        bg={C.bg}
        minH={{ base: "auto", md: "100vh" }}
        py={{ base: 24, md: 32 }}
        px={{ base: 4, md: 6, lg: 8 }}
        overflowX="hidden"
        w="100%"
        maxW="100%"
        minW={0}
      >
        <Box w="100%" maxW="7xl" mx="auto" minW={0}>
          {/* Top: dossier + live video */}
          <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6} mb={8} w="full" minW={0}>
            <GridItem minW={0}>
              <Flex
                position="relative"
                direction={{ base: "column", sm: "row" }}
                align={{ base: "stretch", sm: "center" }}
                justify="space-between"
                gap={6}
                p={{ base: 6, md: 8 }}
                bg={C.paper}
                borderWidth="1px"
                borderColor={C.border}
                borderRadius="lg"
                boxShadow="sm"
                w="full"
                minW={0}
              >
                {userData?.grade?.id === 10 && (
                  <Box position="absolute" top="-10px" left="-10px" zIndex={1}>
                    <Text
                      bg="yellow.400"
                      color="white"
                      fontWeight="800"
                      px={3}
                      py={1}
                      borderRadius="md"
                      fontSize="xs"
                      letterSpacing="0.1em"
                      textTransform="uppercase"
                      boxShadow="md"
                    >
                      Premium
                    </Text>
                  </Box>
                )}
                <Flex align="flex-start" gap={{ base: 4, md: 8 }} flex="1" minW={0}>
                  <Avatar
                    size="xl"
                    borderRadius="lg"
                    src={
                      userData?.subject?.logo
                        ? `${baseUrl}/${userData.subject.logo.replace(/^\//, "")}`
                        : undefined
                    }
                    name={userData?.name}
                    bg="transparent"
                  />
                  <Box minW={0}>
                    <Text
                      fontSize="10px"
                      fontWeight="800"
                      letterSpacing="0.2em"
                      color={`${C.secondary}99`}
                      textTransform="uppercase"
                      mb={1}
                    >
                      Student dossier
                    </Text>
                    <Heading
                      size="lg"
                      color={C.primary}
                      fontWeight="800"
                      mb={4}
                      maxW="100%"
                      wordBreak="break-word"
                    >
                      {userData?.name}
                    </Heading>
                    <Flex gap={10} flexWrap="wrap">
                      <Box>
                        <Text
                          fontSize="9px"
                          fontWeight="700"
                          color={`${C.secondary}66`}
                          textTransform="uppercase"
                          letterSpacing="wider"
                        >
                          Group
                        </Text>
                        <Text fontSize="sm" fontWeight="700" color={C.secondary}>
                          {userData?.group?.name || "—"}
                        </Text>
                      </Box>
                      <Box>
                        <Text
                          fontSize="9px"
                          fontWeight="700"
                          color={`${C.secondary}66`}
                          textTransform="uppercase"
                          letterSpacing="wider"
                        >
                          Subject
                        </Text>
                        <Text fontSize="sm" fontWeight="700" color={C.secondary}>
                          {userData?.subject?.name || "—"}
                        </Text>
                      </Box>
                      <Box>
                        <Text
                          fontSize="9px"
                          fontWeight="700"
                          color={`${C.secondary}66`}
                          textTransform="uppercase"
                          letterSpacing="wider"
                        >
                          Code
                        </Text>
                        <Text fontSize="sm" fontWeight="700" color={C.secondary}>
                          {userData?.user_name || "—"}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                </Flex>
                <VStack
                  spacing={2}
                  p={4}
                  bg={C.mutedBg}
                  borderWidth="1px"
                  borderColor={C.border}
                  borderRadius="lg"
                  align="center"
                  minW={{ base: "full", sm: "140px" }}
                  w={{ base: "full", sm: "auto" }}
                >
                  <Box
                    bg={C.paper}
                    p={2}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={C.border}
                  >
                    <QRCode size={112} value={scanAttendanceUrl(userData?.id)} />
                  </Box>
                  <Text
                    fontSize="9px"
                    fontWeight="800"
                    color={`${C.secondary}99`}
                    textTransform="uppercase"
                    letterSpacing="widest"
                  >
                    Attendance QR
                  </Text>
                </VStack>
              </Flex>
            </GridItem>

            <GridItem minW={0} display="flex" flexDirection="column">
              <Box
                p={6}
                bg={C.paper}
                borderWidth="1px"
                borderColor={C.border}
                borderRadius="lg"
                h="full"
                minH={{ base: "auto", lg: "280px" }}
                display="flex"
                flexDirection="column"
                minW={0}
              >
                <Text
                  fontSize="11px"
                  fontWeight="800"
                  letterSpacing="0.12em"
                  color={C.secondary}
                  textTransform="uppercase"
                  mb={4}
                  flexShrink={0}
                >
                  Live video
                </Text>

                {hasLiveVideo ? (
                  <Flex flex="1" direction="column" justify="space-between" gap={4}>
                    <Flex
                      align="center"
                      gap={3}
                      p={3}
                      bg={C.mutedBg}
                      borderWidth="1px"
                      borderColor={C.border}
                      borderRadius="lg"
                    >
                      <Flex
                        w="52px"
                        h="52px"
                        bg={C.secondary}
                        color={C.cream}
                        borderRadius="lg"
                        align="center"
                        justify="center"
                        flexShrink={0}
                      >
                        <Video size={22} />
                      </Flex>
                      <Box minW={0} flex="1">
                        <Text
                          fontSize="10px"
                          fontWeight="800"
                          color={C.muted}
                          textTransform="uppercase"
                          letterSpacing="0.08em"
                          mb={0.5}
                        >
                          Session
                        </Text>
                        <Text fontSize="sm" fontWeight="700" color={C.secondary} noOfLines={2}>
                          {liveVideo[0]?.title}
                        </Text>
                      </Box>
                    </Flex>
                    <Button
                      w="full"
                      size="sm"
                      bg={C.primary}
                      color={C.cream}
                      fontWeight="800"
                      textTransform="uppercase"
                      fontSize="10px"
                      letterSpacing="0.08em"
                      borderRadius="lg"
                      leftIcon={<Play size={16} />}
                      _hover={{ bg: C.secondary }}
                      onClick={() => {
                        dispatch(setLiveVideoSeenRequest(liveVideo?.[0]?.id));
                        window.open(liveVideo?.[0]?.zoom_link);
                      }}
                    >
                      Join live video
                    </Button>
                  </Flex>
                ) : (
                  <Flex flex="1" direction="column" align="center" justify="center" py={8} gap={3}>
                    <Flex
                      w="52px"
                      h="52px"
                      bg={C.mutedBg}
                      borderWidth="1px"
                      borderColor={C.border}
                      borderRadius="lg"
                      align="center"
                      justify="center"
                      color={C.muted}
                    >
                      <Video size={22} />
                    </Flex>
                    <Text fontSize="sm" color={C.muted} textAlign="center" lineHeight="tall">
                      There isn&apos;t any available live video right now.
                    </Text>
                  </Flex>
                )}
              </Box>
            </GridItem>
          </Grid>

          <Box mb={10} w="full" minW={0}>
            <Box mb={6}>
              <Text
                fontSize="sm"
                fontWeight="800"
                color={C.secondary}
                letterSpacing="0.08em"
                textTransform="uppercase"
              >
                Learning insights
              </Text>
              <Text fontSize="sm" color={C.muted} mt={1}>
                Track your accuracy, monthly progress, and chapters that need the most review.
              </Text>
            </Box>
            <HomeAnalyticsCharts
              performanceData={performanceData}
              isLoading={perfLoading}
              sessionsSlot={
                <Box
                  p={6}
                  bg={C.paper}
                  borderWidth="1px"
                  borderColor={C.border}
                  borderRadius="lg"
                  w="full"
                  minW={0}
                  flex="1"
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                >
                  <Box>
                    <Text
                      fontSize="10px"
                      fontWeight="800"
                      letterSpacing="0.14em"
                      color={C.muted}
                      textTransform="uppercase"
                      mb={3}
                    >
                      {courseSections.length === 0 ? "Current sections" : "Continue learning"}
                    </Text>
                    {courseSections.length === 0 ? (
                      <Text fontSize="sm" color={C.muted} lineHeight="tall">
                        Your sections will show here. Open a section to get started.
                      </Text>
                    ) : (
                      <Box
                        className="scroll"
                        maxH="220px"
                        overflowY="auto"
                        overflowX="hidden"
                        pr={1}
                        css={{
                          scrollbarWidth: "thin",
                          scrollbarColor: `${C.primary} transparent`,
                        }}
                      >
                        <VStack align="stretch" spacing={3}>
                          {courseSections.map((section) => (
                            <Flex
                              key={section.id}
                              as={Link}
                              to={`/videos/chapters/${section.id}?name=${encodeURIComponent(
                                section.name || ""
                              )}`}
                              align="center"
                              gap={2}
                              p={3}
                              bg={C.mutedBg}
                              borderWidth="1px"
                              borderColor={C.border}
                              borderRadius="md"
                              _hover={{
                                borderColor: C.primary,
                                bg: "rgba(232, 93, 4, 0.06)",
                                textDecoration: "none",
                              }}
                              transition="border-color 0.2s, background 0.2s"
                            >
                              <Box minW={0} flex="1">
                                <Text
                                  fontSize="9px"
                                  fontWeight="800"
                                  color={C.primary}
                                  textTransform="uppercase"
                                  letterSpacing="0.08em"
                                  mb={1}
                                >
                                  Section
                                </Text>
                                <Text
                                  fontSize="sm"
                                  fontWeight="700"
                                  color={C.secondary}
                                  noOfLines={2}
                                >
                                  {section.name}
                                </Text>
                              </Box>
                              <Box
                                as="span"
                                display="inline-flex"
                                color={C.primary}
                                flexShrink={0}
                                aria-hidden
                              >
                                <MdChevronRight size={22} />
                              </Box>
                            </Flex>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  </Box>
                  <Button
                    as={Link}
                    to="/videos/sections"
                    mt={5}
                    w="full"
                    size="sm"
                    bg={C.primary}
                    color={C.cream}
                    fontWeight="800"
                    textTransform="uppercase"
                    fontSize="10px"
                    letterSpacing="0.08em"
                    borderRadius="lg"
                    _hover={{ bg: C.secondary }}
                  >
                    {courseSections.length === 0 ? "Browse sections" : "View all sections"}
                  </Button>
                </Box>
              }
            />
          </Box>

          <Box mb={10} w="full" minW={0}>
            <MissedQuestions />
          </Box>

          <Flex
            pt={8}
            pb={4}
            borderTopWidth="1px"
            borderColor={C.border}
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            gap={6}
            align={{ base: "flex-start", md: "flex-end" }}
          >
            <Box maxW="md">
              <Text
                fontSize="10px"
                fontWeight="800"
                letterSpacing="0.12em"
                color={`${C.secondary}66`}
                textTransform="uppercase"
                mb={2}
              >
                Asquera student portal
              </Text>
              <Text fontSize="sm" color={C.muted} lineHeight="tall">
                Your progress and attendance tools in one place.
              </Text>
            </Box>
            <ChakraLink
              as={Link}
              to="/privacy-policy"
              fontSize="9px"
              fontWeight="800"
              letterSpacing="0.08em"
              textTransform="uppercase"
              color={C.secondary}
              _hover={{ color: C.primary }}
            >
              Privacy
            </ChakraLink>
          </Flex>
        </Box>
      </Box>

      <Box position="fixed" bottom={6} right={6} zIndex={50}>
        <Button
          onClick={onChallengeOpen}
          borderRadius="full"
          w="64px"
          h="64px"
          minW="64px"
          p={0}
          bg="linear-gradient(to right, #f97316, #ea580c)"
          color="white"
          boxShadow="lg"
          _hover={{
            bg: "linear-gradient(to right, #ea580c, #c2410c)",
            boxShadow: "xl",
            "& .flame-icon": { transform: "scale(1.1)" },
          }}
          title="Daily Challenge"
          position="relative"
          aria-label="Daily Challenge"
        >
          <Box
            className="flame-icon"
            as="span"
            display="inline-flex"
            transition="transform 0.2s"
          >
            <Flame size={32} />
          </Box>
          {userData?.streak_count > 0 && (
            <Flex
              position="absolute"
              top="-8px"
              right="-8px"
              bg="red.500"
              color="white"
              fontSize="xs"
              fontWeight="bold"
              borderRadius="full"
              w={6}
              h={6}
              align="center"
              justify="center"
            >
              {userData.streak_count}
            </Flex>
          )}
        </Button>
      </Box>

      <AnnouncementsCarousel
        announcements={announcements}
        isOpen={showAnnouncements}
        onClose={handleCloseAnnouncements}
      />
      <DailyChallengeMenu
        isOpen={isChallengeOpen}
        onClose={onChallengeClose}
        userData={userData}
      />
    </Layout>
  );
};

export default withTranslation()(StudentHome);
